import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_URL, formatDate, getStatusBadgeClass } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  FileText,
  Loader2,
  Building2,
  UserCheck,
  TrendingUp,
  DollarSign,
  Settings,
  CreditCard,
  Shield,
  Upload,
  Rss,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Mail,
  MessageSquare,
  MousePointerClick,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Send,
  Clock,
  BadgeCheck,
  ExternalLink,
  XCircle,
  MapPin,
  Bell,
  Eye,
} from 'lucide-react';
import { useAdminCtx } from './AdminContext';


const AdminTabEmployers = () => {
  const { employers, employersLoading, fetchEmployers, handleVerifyEmployer, verifyingEmployer } = useAdminCtx();
  return (
    <>
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">Employer Verification</h3>
                  <p className="text-gray-500 text-sm">
                    Verified employers get a badge on their job listings and appear in "Verified Only" searches
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={fetchEmployers}
                  disabled={employersLoading}
                  data-testid="refresh-employers-btn"
                >
                  {employersLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Refresh
                </Button>
              </div>
              
              {employersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
              ) : employers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Company</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Contact</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Joined</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employers.map((employer) => (
                        <tr key={employer.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {employer.is_verified_employer && (
                                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                              )}
                              <div>
                                <p className="font-medium text-gray-900">{employer.company_name || employer.name}</p>
                                <p className="text-sm text-gray-500">{employer.company_website || '-'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-gray-900">{employer.name}</p>
                            <p className="text-sm text-gray-500">{employer.email}</p>
                          </td>
                          <td className="py-3 px-4 text-gray-600 text-sm">
                            {formatDate(employer.created_at)}
                          </td>
                          <td className="py-3 px-4">
                            {employer.is_verified_employer ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Verified
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500">
                                Not Verified
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {employer.is_verified_employer ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleVerifyEmployer(employer.user_id, false)}
                                disabled={verifyingEmployer === employer.user_id}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`unverify-employer-${employer.user_id}`}
                              >
                                {verifyingEmployer === employer.user_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  'Remove'
                                )}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleVerifyEmployer(employer.user_id, true)}
                                disabled={verifyingEmployer === employer.user_id}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`verify-employer-${employer.user_id}`}
                              >
                                {verifyingEmployer === employer.user_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                )}
                                Verify
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-900 mb-2">No Employers Found</h3>
                  <p className="text-gray-500">No employers have registered yet.</p>
                </div>
              )}
            </div>
    </>
  );
};

export default AdminTabEmployers;
